/**
 * Centralized logging utility
 * Provides different log levels and only logs in development mode
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogOptions {
  context?: string;
  data?: any;
}

class Logger {
  private isDevelopment: boolean;
  private isTest: boolean;

  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.isTest = process.env.NODE_ENV === 'test';
  }

  private formatMessage(level: LogLevel, message: string, options?: LogOptions): string {
    const timestamp = new Date().toISOString();
    const context = options?.context ? `[${options.context}]` : '';
    return `[${timestamp}] ${level.toUpperCase()} ${context} ${message}`;
  }

  private shouldLog(level: LogLevel): boolean {
    // Never log in test environment to avoid polluting test output
    if (this.isTest) {
      return false;
    }

    // In production, disable all console logging
    // Errors should be sent to error tracking service instead
    if (!this.isDevelopment) {
      return false;
    }

    // In development, log everything
    return true;
  }

  /**
   * Log debug information (development only)
   */
  debug(message: string, options?: LogOptions): void {
    if (this.shouldLog('debug')) {
      console.debug(this.formatMessage('debug', message, options), options?.data || '');
    }
  }

  /**
   * Log general information (development only)
   */
  info(message: string, options?: LogOptions): void {
    if (this.shouldLog('info')) {
      console.info(this.formatMessage('info', message, options), options?.data || '');
    }
  }

  /**
   * Log warnings (development only)
   */
  warn(message: string, options?: LogOptions): void {
    if (this.shouldLog('warn')) {
      console.warn(this.formatMessage('warn', message, options), options?.data || '');
    }
  }

  /**
   * Log errors (development only)
   * In production, errors should be sent to error tracking service
   */
  error(message: string, error?: Error | unknown, options?: LogOptions): void {
    if (this.shouldLog('error')) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorStack = error instanceof Error ? error.stack : undefined;

      console.error(
        this.formatMessage('error', message, options),
        errorMessage,
        errorStack || '',
        options?.data || ''
      );
    }
    
    // In production, you might want to send errors to an error tracking service
    // Example: Sentry.captureException(error);
    // This can be implemented here without console statements
  }

  /**
   * Create a logger instance with a specific context
   */
  withContext(context: string) {
    return {
      debug: (message: string, data?: any) => this.debug(message, { context, data }),
      info: (message: string, data?: any) => this.info(message, { context, data }),
      warn: (message: string, data?: any) => this.warn(message, { context, data }),
      error: (message: string, error?: Error | unknown, data?: any) =>
        this.error(message, error, { context, data }),
    };
  }
}

// Export singleton instance
export const logger = new Logger();

// Convenience method for creating context loggers
export const createLogger = (context: string) => logger.withContext(context);
